import { useState, useRef, useMemo } from "react";
import { useQueryClient, useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
// import { Seller } from "@api/apiClient";
// import AddSeller from "@components/AddSeller";
import { getSellers, Seller } from "@api/apiClient/sellers";
import { Modal, ModalSize } from "@components/new/Modal";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Marketplace } from "@components/new/Marketplace";
import { TabsList, TabsContent } from "@components/new/Tabs/Tabs";
import WBForm from "./WBForm";
import YMForm from "./YMForm";
import AliForm from "./AliForm";
import OzonForm from "./OzonForm";
import { useProfile } from '@contexts/ProfileContext';
import { FormHandle } from "./types";

interface AddShopModalProps {
  onClose: () => void;
}

interface ISubmitButtonProps {
  disabled: boolean;
  title?: string;
  isLoading?: boolean;
}

enum steps {
  FORM = "form",
  CONFIRM = "confirm",
  SUCCESS = "success",
  ERROR = "error",
}

const AddShopModal = ({ onClose }: AddShopModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [ activeTab, setActiveTab ] = useState("wb");
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    title: "",
    isLoading: false,
  });
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => {} });
  const { account } = useProfile();

  const { data: sellersData } = useQuery({
    queryKey: ["sellers"],
    queryFn: () => getSellers(),
    retry: 1,
  });

  const knownEmails = useMemo(() => {
    return [
      (account?.email ?? ""),
      ...(sellersData?.items || []).map((v: Seller) => (v.email ?? ""))
    ];
  }, [sellersData?.items, account?.email])

  const handleButtonProps = (props: ISubmitButtonProps) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      title: props.title ?? "",
      isLoading: props.isLoading ?? false,
    })
  }

  const onSuccess = () => {
    queryClient.invalidateQueries(
      { queryKey: ["sellers"] }
    );
    queryClient.invalidateQueries(
      { queryKey: ["parcels-page"] }
    );
    queryClient.invalidateQueries(
      { queryKey: ["returns-page"] }
    );
    setStep(steps.SUCCESS);
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const tabs = [
    {
      id: "wb",
      icon: () => (<Marketplace name="Wildberries" />),
      title: "Wildberries",
      content: (
        <WBForm
          formRef={formRef}
          onStatusChange={handleButtonProps}
          onSuccess={onSuccess}
          onError={onError}
          knownEmails={knownEmails}
        />
      ),
    },
    {
      id: "ym",
      icon: () => (<Marketplace name="Yandex" />),
      title: "Yandex Market",
      content: (
        <YMForm
          formRef={formRef}
          onStatusChange={handleButtonProps}
          onSuccess={onSuccess}
          onError={onError}
          knownEmails={knownEmails}
        />
      ),
    },
    {
      id: "ali",
      icon: () => (<Marketplace name="Aliexpress" />),
      title: "AliExpress",
      content: (
        <AliForm
          formRef={formRef}
          onStatusChange={handleButtonProps}
          onSuccess={onSuccess}
          onError={onError}
          knownEmails={knownEmails}
        />
      ),
    },
    {
      id: "ozon",
      icon: () => (<Marketplace name="Ozon" />),
      title: "Ozon",
      content: (
        <OzonForm
          formRef={formRef}
          onStatusChange={handleButtonProps}
          onSuccess={onSuccess}
          onError={onError}
          knownEmails={knownEmails}
        />
      ),
    },
  ]

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('storesPage.addShopModal.title')}
        onClose={onClose}
        size={ModalSize.M}
        footerButtons={[
          {
            title: t('storesPage.addShopModal.successBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          title={t('storesPage.addShopModal.successTitle')}
          description={t('storesPage.addShopModal.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('storesPage.addShopModal.title')}
        onClose={onClose}
        size={ModalSize.M}
        footerButtons={[
          {
            title: t('storesPage.addShopModal.errorCancelBtn'),
            onClick: onClose,
          },
          {
            title: t('storesPage.addShopModal.errorRepeateBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('storesPage.addShopModal.errorTitle')}
          description={t('storesPage.addShopModal.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      title={t('storesPage.addShopModal.title')}
      size={ModalSize.M}
      onClose={onClose}
      confirmCancel
      footerButtons={[
        {
          title: formRef.current?.back ? t('storesPage.addShopModal.backBtn') : t('storesPage.addShopModal.cancelBtn'),
          onClick: formRef.current?.back
        },
        {
          title: buttonProps.title || t('storesPage.addShopModal.nextBtn'),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      {formRef.current?.back ? null : (
        <TabsList
          tabs={tabs}
          onChange={setActiveTab}
          value={activeTab}
          stretch
        />
      )}

      <TabsContent
        tab={tabs.find((tab) => (tab.id === activeTab))}
      />

    </Modal>
  )
}

export default AddShopModal;
